import React, { useCallback, useEffect, useRef, useState } from 'react';
import moment from 'moment';
import 'moment/locale/es';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { StaticDateTimePicker } from '@mui/x-date-pickers/StaticDateTimePicker';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import {
	Box,
	Popover,
	TextField,
	Typography,
	Stack,
	Divider,
	InputAdornment,
	FormHelperText
} from '@mui/material';
import { PickersDay } from '@mui/x-date-pickers/PickersDay';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import { CalendarToday, DeleteOutlineOutlined } from '@mui/icons-material';
import useFlexBoxStyles from 'common/flexBox';
import ActionButton from './ActionButton';
import CustomTooltip from './CustomTooltip';
import CustomIconButton from './CustomIconButton';

const useStyles = makeStyles()((theme) => {
	return {
		root: {
			'& .MuiInputBase-root': {
				height: theme.spacing(4.5),
				background: '#FFFF',
				borderRadius: theme.spacing(4.5),
				paddingRight: 0,
				'& > fieldset': {
					borderColor: '#E2E2E2',
				},
				'& > input': {
					padding: theme.spacing(0, 1),
					height: '100%',
					fontSize: '12px',
					fontWeight: '500',
					position: 'relative',
					'&:focus': {
						borderRadius: theme.spacing(4.5),
					},
				},
			},

			'& .MuiFormHelperText-root': {
				[theme.breakpoints.up('xl')]: {
					fontSize: '14px',
				},
			},
		},
		rootMultiline: {
			'& .MuiInputBase-root': {
				height: 'auto',
				borderRadius: theme.spacing(2.5),
			},
		},
		mW12: {
			minWidth: theme.spacing(14),
		},
		calendarRoot: {
			'.MuiPickersCalendarHeader-label, .MuiPickersDay-root, .MuiDayPicker-weekDayLabel': {
				...theme.typography.subtitle2,
			},
		},
		leftClock: {
            '& [class*="MuiClockPicker-arrowSwitcher"]': {
                left: '12px',
                right: '0px',
            },
        },
	};
});

const CustomDateTimeRangePicker = ({
	date,
	dates = [],
	onApply,
	onDelete,
	disabled,
	error,
}) => {
	const { t } = useTranslation();
	const { classes, cx } = useStyles();
	const { classes: flexBox } = useFlexBoxStyles();

	const getInitialRange = useCallback(() => {
        if (date?.start && date?.end) {
            return {
                initialStart: moment(date.start),
                initialEnd: moment(date.end),
                isProvisional: false
            };
        } else if (dates.length > 0) {
            const lastRange = dates[dates.length - 1];
            const initialStart = moment(lastRange.endDate).clone().add(1, "hour");
            const initialEnd = initialStart.clone().add(1, "hour");
            return { initialStart, initialEnd, isProvisional: true };
        } else {
            const now = moment();
            return {
                initialStart: now,
                initialEnd: now.clone().add(1, "hour"),
                isProvisional: true
            };
        }
    }, [dates, date]);

    const { initialStart, initialEnd, isProvisional } = getInitialRange();

    const [start, setStart] = useState(moment(initialStart));
    const [end, setEnd] = useState(moment(initialEnd));
    const [tempStart, setTempStart] = useState(moment(initialStart));
    const [tempEnd, setTempEnd] = useState(moment(initialEnd));

    const [anchorEl, setAnchorEl] = useState(null);

	const startFieldRef = useRef(null);
	const endFieldRef = useRef(null);

	useEffect(() => {
        const { initialStart, initialEnd } = getInitialRange();
        setStart(moment(initialStart));
        setEnd(moment(initialEnd));
        setTempStart(moment(initialStart));
        setTempEnd(moment(initialEnd));
    }, [getInitialRange]);

	const handleOpen = (which) => () => {
		if (disabled) return;
		setTempStart(start);
		setTempEnd(end);
		setAnchorEl(which === 'start' ? startFieldRef.current : endFieldRef.current);
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	const handleApply = () => {
		setStart(tempStart);
		setEnd(tempEnd);
		onApply?.({
			start: moment(tempStart).format("YYYY-MM-DDTHH:mm"),
			end: moment(tempEnd).format("YYYY-MM-DDTHH:mm"),
		});
		handleClose();
	};

	const handleDelete = () => {
        onDelete?.();
    };

	const open = Boolean(anchorEl);

	const handleTempStartChange = (newValue) => {
		setTempStart(newValue);
		if (newValue && tempEnd && newValue.isAfter(tempEnd)) {
			setTempEnd(newValue.clone().add(1, "hour"));
		}
	};

	const handleTempEndChange = (newValue) => {
		if (tempStart && newValue && newValue.isBefore(tempStart)) {
			setTempEnd(tempStart.clone().add(1, "hour"));
		} else {
			setTempEnd(newValue);
		}
	};

	const renderRangeDay = (date, _selectedDates, pickersDayProps) => {
		if (!tempStart || !tempEnd) {
			return <PickersDay {...pickersDayProps} />;
		}

		const isStart = date.isSame(tempStart, "day");
		const isEnd = date.isSame(tempEnd, "day");
		const isInRange =
			date.isAfter(tempStart, "day") && date.isBefore(tempEnd, "day");

		return (
			<PickersDay
				{...pickersDayProps}
				sx={{
					...(isInRange && {
						backgroundColor: "rgba(255, 0, 0, 0.3)",
						borderRadius: 0
					}),
					...(isStart && {
						backgroundColor: "rgba(255, 0, 0, 0.5)",
						color: "#000000",
						borderRadius: "50%"
					}),
					...(isEnd && {
						backgroundColor: "rgba(255, 0, 0, 0.5)",
						color: "#000000",
						borderRadius: "50%"
					}),
					"&:hover": {
						backgroundColor: isStart || isEnd
							? "rgba(255, 0, 0, 0.7)"
							: "rgba(255, 0, 0, 0.5)",
						color: isStart || isEnd ? "#ffffff" : "#000000"
					}
				}}
			/>
		);
	};

	const RangeTextField = ({
		value,
		onClick,
		isProvisional,
		cx,
		classes,
		label,
	}) => (
		<TextField
			className={cx(classes.root, classes.mW12)}
			fullWidth
			variant="outlined"
			size="small"
			value={value}
			InputProps={{
				readOnly: true,
				endAdornment: (
					<InputAdornment position="end" style={{ marginRight: 8 }}>
						<CalendarToday style={{ fontSize: 16 }} />
					</InputAdornment>
				),
				style: isProvisional ? { color: '#00000070' } : undefined
			}}
			onClick={onClick}
			label={label}
		/>
	);

	const isValidDateRange = () => {
		if (!tempStart || !tempEnd) return false;
		return tempStart.isBefore(tempEnd);
	};

	return (
		<LocalizationProvider dateAdapter={AdapterMoment}>
			<Stack direction="row" alignItems="center" justifyContent="space-between">
				<span ref={startFieldRef} style={{ display: 'inline-block' }}>
					<RangeTextField
						value={start ? start.format("DD/MM/YYYY HH:mm") : ""}
						onClick={handleOpen('start')}
						isProvisional={isProvisional}
						cx={cx}
						classes={classes}
					/>
				</span>
				<Typography variant="body1" style={{ margin: '0 8px' }}>
					{t('services.form.dateTo').toUpperCase()}
				</Typography>
				<span ref={endFieldRef} style={{ display: 'inline-block' }}>
					<RangeTextField
						value={end ? end.format("DD/MM/YYYY HH:mm") : ""}
						onClick={handleOpen('end')}
						isProvisional={isProvisional}
						cx={cx}
						classes={classes}
					/>
				</span>
				{onDelete ? (
					<CustomTooltip arrow title={t('contact.remove')} placement="bottom">
						<CustomIconButton
							aria-label="delete"
							onClick={handleDelete}
							size="small"
							disabled={(!date?.start && !date?.end) || !isValidDateRange()}
							style={{ marginLeft: 8 }}
						>
							<DeleteOutlineOutlined />
						</CustomIconButton>
					</CustomTooltip>
				) : null}
			</Stack>
			{error ? (
				<Stack direction="row" alignItems="center" justifyContent="center">
					<FormHelperText error>{error}</FormHelperText>
				</Stack>
			) : null}

			<Popover
				open={open}
				anchorEl={anchorEl}
				onClose={handleClose}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "left"
				}}
				slotProps={{
					paper: {
						style: { borderRadius: '16px' }
					}
				}}
			>
				<Box sx={{ display: "flex", justifyContent: "space-around", p: 2, gap: 2 }}>
					<Typography variant="subtitle2" fontWeight={400} className={flexBox.paddingTop1}>
                        {t('services.form.dateFrom').toUpperCase()}: {tempStart ? tempStart.format("DD/MM/YYYY HH:mm") : "--"}
                    </Typography>
					<Typography variant="subtitle2" fontWeight={400} className={flexBox.paddingTop1}>
                        {t('services.form.dateTo').toUpperCase()}: {tempEnd ? tempEnd.format("DD/MM/YYYY HH:mm") : "--"}
                    </Typography>
				</Box>

				<Box sx={{ display: "flex", p: 2, gap: 2 }}>
					<Box className={classes.leftClock}>
						<StaticDateTimePicker
							classes={{ root: classes.calendarRoot }}
							ampm={false}
							displayStaticWrapperAs="desktop"
							value={tempStart}
							onChange={handleTempStartChange}
							renderDay={renderRangeDay}
							renderInput={() => null}
						/>
					</Box>

					<Divider orientation="vertical" flexItem />

					<Box>
						<StaticDateTimePicker
							classes={{ root: classes.calendarRoot }}
							ampm={false}
							displayStaticWrapperAs="desktop"
							value={tempEnd}
							onChange={handleTempEndChange}
							minDateTime={tempStart || undefined}
							renderDay={renderRangeDay}
							referenceDate={
								tempStart ? tempStart.clone().add(1, "month") : moment()
							}
							renderInput={() => null}
						/>
					</Box>
				</Box>

				<Stack
					direction="row"
					justifyContent="flex-end"
					alignItems="center"
					spacing={1}
					className={cx(flexBox.paddingBottom2, flexBox.paddingRight2)}
				>
					<ActionButton onClick={handleClose} color="default" fitWidth>
						{t('app.common.cancel')}
					</ActionButton>

					<ActionButton
						color="primary"
						autoFocus
						fitWidth
						disabled={!isValidDateRange()}
						onClick={handleApply}
					>
						{t('app.common.save')}
					</ActionButton>
				</Stack>
			</Popover>
		</LocalizationProvider>
	);
};

export default CustomDateTimeRangePicker;
